import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Tabs,
    Space,
    Button,
    Tag,
    Spin,
    message,
    Tooltip,
    Alert,
    Select,
    Popover,
    Modal,
} from 'antd';
import {
    CloseOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { Lead, getLead, updateLead, LeadStatus } from '../../services/lead';
import { getCurrentUser } from '../../services/auth';
import { getEntityCollaborators } from '../../services/collaborator';
import QuickFollowUp from './QuickFollowUp';
import FollowUpTimeline from './FollowUpTimeline';
import LeadDetailForm from './LeadDetailForm';
import FinanceTab from './FinanceTab';
import CallRecordsTab from './CallRecordsTab';
import OutboundCallButton from '../OutboundCallButton';
import './styles.css';

const { TabPane } = Tabs;

interface LeadDrawerProps {
    leadId: number | null;
    visible: boolean;
    onClose: () => void;
    onUpdate?: () => void;
    onSwitchLead?: (leadId: number) => void;
}

const LeadDrawer: React.FC<LeadDrawerProps> = ({
    leadId,
    visible,
    onClose,
    onUpdate,
    onSwitchLead,
}) => {
    const [loading, setLoading] = useState(false);
    const [lead, setLead] = useState<Lead | null>(null);
    const [activeTab, setActiveTab] = useState('info');
    const [editMode, setEditMode] = useState(false);
    const [tags, setTags] = useState<string[]>([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [currentUserId, setCurrentUserId] = useState<number | null>(null);
    const [tagPopoverVisible, setTagPopoverVisible] = useState(false);
    const [selectedTag, setSelectedTag] = useState<string | undefined>(undefined);

    // 检查是否有未提交的跟进内容
    const checkUnsavedFollowUp = () => {
        if (!leadId) return false;
        const storageKey = `quick_follow_up_${leadId}`;
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
            try {
                const parsed = JSON.parse(savedData);
                return (parsed.content && parsed.content.trim().length > 0) || 
                       (parsed.attachments && parsed.attachments.length > 0);
            } catch {
                return false;
            }
        }
        return false;
    };

    // 处理关闭
    const handleClose = () => {
        if (checkUnsavedFollowUp()) {
            Modal.confirm({
                title: '确认关闭',
                content: '您有未提交的跟进内容，关闭后数据将保存在本地，下次打开时可以继续填写',
                okText: '确认',
                cancelText: '取消',
                onOk: () => {
                    onClose();
                },
            });
        } else {
            onClose();
        }
    };

    // 标签选项
    const TAG_OPTIONS = ['无意向', '高意向', '中意向', '低意向'];

    // Load lead details
    const loadLeadDetails = async () => {
        if (!leadId) return;

        setLoading(true);
        try {
            const data = await getLead(leadId);
            setLead(data);
            // Parse tags if stored as JSON string
            if (data.tags) {
                try {
                    const parsedTags = typeof data.tags === 'string'
                        ? JSON.parse(data.tags)
                        : data.tags;
                    setTags(Array.isArray(parsedTags) ? parsedTags : []);
                } catch {
                    setTags([]);
                }
            }
        } catch (error) {
            message.error('加载线索详情失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadCurrentUserId = async () => {
            try {
                const user = await getCurrentUser();
                setCurrentUserId(user.id);
            } catch (error) {
                console.error('Failed to load current user', error);
            }
        };
        loadCurrentUserId();
    }, []);

    useEffect(() => {
        if (visible && leadId) {
            loadLeadDetails();
            setActiveTab('info');
            setEditMode(false);
        }
    }, [visible, leadId]);

    // 检查是否是公海池线索
    const isPublicPool = lead?.status === LeadStatus.PUBLIC_POOL;
    
    // 检查是否是创建人
    const isCreator = lead?.created_by === currentUserId;
    
    // 检查是否是负责人
    const isAssignee = lead?.assigned_to === currentUserId;
    
    // 检查是否是协作人（需要从协作人列表中获取）
    const [isCollaborator, setIsCollaborator] = useState(false);
    
    useEffect(() => {
        const checkCollaborator = async () => {
            if (!lead?.id || !currentUserId) {
                setIsCollaborator(false);
                return;
            }
            try {
                const collaborators = await getEntityCollaborators('lead', lead.id);
                const isCollab = collaborators.some(c => c.user_id === currentUserId);
                setIsCollaborator(isCollab);
            } catch (error) {
                console.error('Failed to check collaborator status', error);
                setIsCollaborator(false);
            }
        };
        checkCollaborator();
    }, [lead?.id, currentUserId]);
    
    // 权限判断：
    // 1. 公海池线索：只有创建人可以编辑基本信息，所有人都不能发布跟进
    // 2. 已分配线索：只有负责人和协作人可以编辑和跟进
    const canEdit = isPublicPool 
        ? isCreator 
        : (isAssignee || isCollaborator);
    const canFollowUp = !isPublicPool && (isAssignee || isCollaborator);

    // Handle follow-up success
    const handleFollowUpSuccess = () => {
        setRefreshTrigger(prev => prev + 1); // Trigger timeline refresh
        loadLeadDetails();
        onUpdate?.();
    };

    // Handle lead update
    const handleLeadUpdate = async (values: any) => {
        if (!leadId) return;

        try {
            await updateLead(leadId, values);
            message.success('更新成功');
            setEditMode(false);
            loadLeadDetails();
            onUpdate?.();
        } catch (error) {
            message.error('更新失败');
        }
    };

    // Handle add tag
    const handleAddTag = async (tag: string) => {
        if (!leadId || !tag.trim()) return;

        // 检查是否已存在
        if (tags.includes(tag.trim())) {
            message.warning('该标签已存在');
            return;
        }

        const newTags = [...tags, tag.trim()];
        setTags(newTags);

        try {
            await updateLead(leadId, { tags: newTags });
            message.success('标签添加成功');
            setTagPopoverVisible(false);
            setSelectedTag(undefined);
            onUpdate?.();
        } catch (error) {
            message.error('标签添加失败');
            setTags(tags); // Rollback
        }
    };

    // Handle tag select
    const handleTagSelect = (value: string) => {
        setSelectedTag(value);
        handleAddTag(value);
    };

    // Handle remove tag
    const handleRemoveTag = async (tagToRemove: string) => {
        if (!leadId) return;

        const newTags = tags.filter(t => t !== tagToRemove);
        setTags(newTags);

        try {
            await updateLead(leadId, { tags: newTags });
            message.success('标签删除成功');
            onUpdate?.();
        } catch (error) {
            message.error('标签删除失败');
            setTags(tags); // Rollback
        }
    };

    return (
        <Drawer
            title={null}
            placement="right"
            width={680}
            open={visible}
            onClose={handleClose}
            className="lead-drawer"
            closable={false}
            styles={{ body: { padding: 0 } }}
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                    <Spin size="large" />
                </div>
            ) : lead ? (
                <div className="lead-drawer-content">
                    {/* Header */}
                    <div className="lead-drawer-header">
                        <div className="lead-drawer-header-top">
                            <Space size="middle" style={{ flex: 1 }}>
                                <div className="lead-avatar">
                                    {lead.contact_name?.charAt(0) || 'L'}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h2 className="lead-name">{lead.contact_name}</h2>
                                    <Space size="small" wrap>
                                        {tags.map(tag => (
                                            <Tag
                                                key={tag}
                                                closable={canEdit}
                                                onClose={() => canEdit && handleRemoveTag(tag)}
                                            >
                                                {tag}
                                            </Tag>
                                        ))}
                                    </Space>
                                </div>
                            </Space>
                            <Space>
                                {/* 外呼按钮 */}
                                {lead.phone && canFollowUp && (
                                    <OutboundCallButton
                                        leadId={leadId!}
                                        leadName={lead.contact_name || ''}
                                        phone={lead.phone}
                                        onCallComplete={() => setRefreshTrigger(prev => prev + 1)}
                                        onOpenLeadDetail={(newLeadId) => {
                                            // 需要通知父组件切换到新的线索
                                            // 但是 LeadDrawer 没有这个回调，需要添加
                                            if (onSwitchLead) {
                                                onSwitchLead(newLeadId);
                                            }
                                        }}
                                    />
                                )}
                                {canEdit && (
                                    <Popover
                                        content={
                                            <div style={{ width: 200 }}>
                                                <Select
                                                    placeholder="选择标签"
                                                    style={{ width: '100%' }}
                                                    value={selectedTag}
                                                    onChange={handleTagSelect}
                                                    options={TAG_OPTIONS
                                                        .filter(option => !tags.includes(option))
                                                        .map(option => ({
                                                            label: option,
                                                            value: option,
                                                        }))}
                                                />
                                            </div>
                                        }
                                        title="添加标签"
                                        trigger="click"
                                        open={tagPopoverVisible}
                                        onOpenChange={setTagPopoverVisible}
                                    >
                                        <Tooltip title="添加标签">
                                            <Button
                                                type="text"
                                                icon={<PlusOutlined />}
                                            />
                                        </Tooltip>
                                    </Popover>
                                )}
                                <Tooltip title="关闭">
                                    <Button
                                        type="text"
                                        icon={<CloseOutlined />}
                                        onClick={handleClose}
                                    />
                                </Tooltip>
                            </Space>
                        </div>
                    </div>

                    {/* Tabs */}
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        className="lead-drawer-tabs"
                    >
                        <TabPane tab="线索资料" key="info">
                            <div className="lead-drawer-body">
                                {/* 权限提示 */}
                                {isPublicPool && (
                                    <Alert
                                        message="公海池线索"
                                        description={
                                            isCreator 
                                                ? "此线索在公海池中，您可以编辑基本信息，但不能发布跟进记录。认领后即可正常跟进。"
                                                : "此线索在公海池中，仅可查看。认领后即可编辑和跟进。"
                                        }
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                )}
                                {!isPublicPool && !canEdit && (
                                    <Alert
                                        message="无编辑权限"
                                        description="您不是此线索的负责人或协作人，仅可查看。"
                                        type="warning"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                )}

                                {/* Quick Follow-up - 仅非公海池线索显示 */}
                                {canFollowUp && (
                                    <QuickFollowUp
                                        leadId={leadId!}
                                        lead={lead}
                                        onSuccess={handleFollowUpSuccess}
                                        onLeadUpdate={(updatedLead) => {
                                            setLead(updatedLead);
                                            setRefreshTrigger(prev => prev + 1);
                                        }}
                                    />
                                )}

                                {/* Follow-up Timeline */}
                                <FollowUpTimeline 
                                    leadId={leadId!} 
                                    refreshTrigger={refreshTrigger}
                                />

                                {/* Lead Details */}
                                <LeadDetailForm
                                    lead={lead}
                                    editMode={editMode}
                                    onEdit={() => setEditMode(true)}
                                    onCancel={() => setEditMode(false)}
                                    onSave={handleLeadUpdate}
                                    canEdit={canEdit}
                                />
                            </div>
                        </TabPane>

                        <TabPane tab="财务情况" key="finance">
                            <div className="lead-drawer-body">
                                <FinanceTab
                                    leadId={leadId!}
                                    lead={lead}
                                    canEdit={canEdit}
                                />
                            </div>
                        </TabPane>

                        <TabPane tab="外呼记录" key="calls">
                            <div className="lead-drawer-body">
                                <CallRecordsTab
                                    leadId={leadId!}
                                    refreshTrigger={refreshTrigger}
                                />
                            </div>
                        </TabPane>
                    </Tabs>
                </div>
            ) : null}
        </Drawer>
    );
};

export default LeadDrawer;
